import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApolloClient, useMutation } from '@apollo/client';
import { Alert, AlertTitle, alpha, Box, Button, Chip, CircularProgress, TextField, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import SendIcon from '@mui/icons-material/Send';

import { useToast } from '../../../common/hooks/useToast';
import { CrmPageFrame } from '../CrmPageFrame';
import { CRM_PATHS, emailCampaignPath } from '../crmPaths';
import { SegmentSelect } from '../outreach/Pickers';
import { AudienceBreakdown, BodyEditor, EmailPreview, MergeFieldMenu } from './ComposerParts';
import { CRM_CREATE_EMAIL_CAMPAIGN, CRM_UPDATE_EMAIL_CAMPAIGN } from './email.queries';
import {
  Campaign,
  changedFields,
  describeEmailError,
  DraftFields,
  draftProblems,
  EMPTY_DRAFT,
  fieldOfError,
  fieldProblems,
  fieldsOf,
  LIMITS,
  MISSING_LABEL,
} from './emailModel';
import { ScheduleDialog, TestSendDialog } from './SendDialogs';

/**
 * The composer (plan 4.4): who it goes to, who it is from, the subject, the
 * preheader and the body, beside a live preview. A new campaign is made on its
 * first save and the page moves to its own address; after that each save sends
 * only what changed. A test or a send saves first.
 */
interface CampaignEditorProps {
  campaign?: Campaign;
  onChanged?: () => void;
  /** Who a new campaign starts going to: the list or saved filter it was started from. */
  audience?: { id: number; name: string };
}

export const CampaignEditor = ({ campaign, onChanged, audience }: CampaignEditorProps) => {
  const navigate = useNavigate();
  const client = useApolloClient();
  const toast = useToast();

  const [saved, setSaved] = useState<DraftFields>(() => (campaign ? fieldsOf(campaign) : EMPTY_DRAFT));
  const [draft, setDraft] = useState<DraftFields>(() => (!campaign && audience ? { ...saved, segmentId: audience.id, segmentName: audience.name } : saved));
  const [tried, setTried] = useState(false);
  const [dialog, setDialog] = useState<'test' | 'schedule' | null>(null);

  const [create, creating] = useMutation<{ crmCreateEmailCampaign: Campaign }>(CRM_CREATE_EMAIL_CAMPAIGN);
  const [update, updating] = useMutation<{ crmUpdateEmailCampaign: Campaign }>(CRM_UPDATE_EMAIL_CAMPAIGN);
  const saving = creating.loading || updating.loading;
  const saveError = creating.error ?? updating.error;

  const forWorkflow = (campaign?.workflowStepCount ?? 0) > 0;
  const dirty = !campaign || Object.keys(changedFields(draft, saved)).length > 0;
  const fields = fieldProblems(draft);
  const ready = draftProblems(draft, !forWorkflow);
  const set = (patch: Partial<DraftFields>) => setDraft((d) => ({ ...d, ...patch }));

  // Leaving with unsaved changes asks first.
  useEffect(() => {
    if (!dirty || !campaign) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, campaign]);

  const forgetLists = () => {
    client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'crmEmailCampaigns' });
    client.cache.gc();
  };

  /** Saves, and answers the campaign's id, or null when it could not. */
  const save = async (quiet = false): Promise<number | null> => {
    setTried(true);
    if (Object.keys(fields).length) return null;
    try {
      if (!campaign) {
        const input = Object.fromEntries(Object.entries(changedFields(draft, EMPTY_DRAFT)).filter(([, v]) => v !== null));
        const { data } = await create({ variables: { input } });
        forgetLists();
        const made = data!.crmCreateEmailCampaign;
        toast.success(`Saved ${made.name} as a draft.`);
        navigate(emailCampaignPath(made.id), { replace: true });
        return made.id;
      }
      const input = changedFields(draft, saved);
      if (Object.keys(input).length) {
        const { data } = await update({ variables: { id: campaign.id, input } });
        const now = fieldsOf(data!.crmUpdateEmailCampaign);
        setSaved(now);
        setDraft((d) => ({ ...d, segmentName: now.segmentName }));
        forgetLists();
        if (!quiet) toast.success('Saved.');
      }
      return campaign.id;
    } catch {
      return null;
    }
  };

  const openAfterSave = async (which: 'test' | 'schedule') => {
    if (which === 'schedule' && ready.missing.length) {
      setTried(true);
      return;
    }
    const id = await save(true);
    if (id !== null && campaign) setDialog(which);
  };

  const errorField = fieldOfError(saveError);
  const fieldError = (key: keyof DraftFields) => (tried ? fields[key] : undefined) ?? (errorField === key ? describeEmailError(saveError) : undefined);

  const actions = (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      <Button
        variant={campaign ? 'outlined' : 'contained'}
        onClick={() => save()}
        disabled={saving || (!!campaign && !dirty)}
        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveOutlinedIcon />}
      >
        {campaign && !dirty ? 'Saved' : 'Save draft'}
      </Button>
      {campaign && (
        <Button variant="outlined" onClick={() => openAfterSave('test')} disabled={saving} startIcon={<ScienceOutlinedIcon />}>
          Send a test
        </Button>
      )}
      {campaign && !forWorkflow && (
        <Button variant="contained" onClick={() => openAfterSave('schedule')} disabled={saving} startIcon={<SendIcon />}>
          Send or schedule
        </Button>
      )}
    </Box>
  );

  return (
    <CrmPageFrame
      title={campaign ? draft.name || campaign.name : 'New campaign'}
      crumbs={[{ label: 'Email', to: CRM_PATHS.email }, { label: campaign ? campaign.name : 'New campaign' }]}
      actions={actions}
    >
      <Box sx={{ display: 'grid', gap: 2 }}>
        {forWorkflow && (
          <Alert severity="info">
            <AlertTitle>A workflow sends this email</AlertTitle>
            Each person gets it when they reach its step, so changes here reach whoever gets there next. It cannot also go as a blast: duplicate it for that.
          </Alert>
        )}
        {saveError && !errorField && <Alert severity="error">{describeEmailError(saveError, 'It could not be saved.')}</Alert>}

        {campaign && <Readiness ready={ready} forWorkflow={forWorkflow} highlight={tried} />}

        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) minmax(0, 0.9fr)' }, alignItems: 'start' }}>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Card title="Campaign">
              <TextField
                label="Name"
                value={draft.name}
                onChange={(event) => set({ name: event.target.value })}
                error={!!fieldError('name')}
                helperText={fieldError('name') ?? 'For you and other admins; recipients never see it.'}
                inputProps={{ maxLength: LIMITS.name }}
                autoFocus={!campaign}
              />
            </Card>

            {!forWorkflow && (
              <Card title="Who it goes to">
                <SegmentSelect
                  label="List or saved filter"
                  value={draft.segmentId ? { id: draft.segmentId, name: draft.segmentName ?? `#${draft.segmentId}` } : null}
                  onChange={(segment) => set({ segmentId: segment?.id ?? null, segmentName: segment?.name ?? null })}
                  error={tried && ready.missing.includes('AUDIENCE') ? 'Choose who it goes to.' : undefined}
                />
                {draft.segmentId && <AudienceBreakdown segmentId={draft.segmentId} />}
              </Card>
            )}

            <Card title="From">
              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                <TextField label="From name" placeholder="I Love Black People" value={draft.fromName} onChange={(event) => set({ fromName: event.target.value })} inputProps={{ maxLength: LIMITS.fromName }} />
                <TextField
                  label="From address"
                  placeholder="The server's default sender"
                  value={draft.fromEmail}
                  onChange={(event) => set({ fromEmail: event.target.value })}
                  error={!!fieldError('fromEmail')}
                  helperText={fieldError('fromEmail') ?? 'Must be a verified sender in SendGrid.'}
                  inputProps={{ maxLength: LIMITS.email }}
                />
              </Box>
            </Card>

            <Card title="Content" action={<MergeFieldMenu onInsert={(tag) => set({ subject: `${draft.subject}${draft.subject && !draft.subject.endsWith(' ') ? ' ' : ''}${tag}` })} />}>
              <TextField
                label="Subject"
                value={draft.subject}
                onChange={(event) => set({ subject: event.target.value })}
                error={(tried && ready.missing.includes('SUBJECT')) || !!fieldError('subject')}
                helperText={fieldError('subject') ?? `${draft.subject.length} / ${LIMITS.subject}. Merge field adds to the subject.`}
                inputProps={{ maxLength: LIMITS.subject }}
              />
              <TextField
                label="Preheader"
                value={draft.preheader}
                onChange={(event) => set({ preheader: event.target.value })}
                helperText="The line after the subject in most inboxes."
                inputProps={{ maxLength: LIMITS.preheader }}
              />
              <BodyEditor value={draft.bodyHtml} onChange={(bodyHtml) => set({ bodyHtml })} />
              <Typography variant="caption" color="text.secondary">
                Every email ends with the postal address and, unless the body has its own, an unsubscribe link. One click unsubscribes from Gmail and Yahoo too.
              </Typography>
            </Card>
          </Box>

          <Box sx={{ position: { lg: 'sticky' }, top: 16 }}>
            <EmailPreview fromName={draft.fromName} subject={draft.subject} preheader={draft.preheader} bodyHtml={draft.bodyHtml} />
          </Box>
        </Box>
      </Box>

      {campaign && dialog === 'test' && <TestSendDialog campaignId={campaign.id} onClose={() => setDialog(null)} />}
      {campaign && dialog === 'schedule' && (
        <ScheduleDialog
          campaign={{ id: campaign.id, name: draft.name, segmentId: draft.segmentId, segmentName: draft.segmentName }}
          onClose={() => setDialog(null)}
          onScheduled={(c) => {
            setDialog(null);
            forgetLists();
            toast.success(c.scheduledAt && new Date(c.scheduledAt).getTime() > Date.now() + 60_000 ? 'Scheduled.' : 'On its way: it starts within a minute.');
            onChanged?.();
          }}
        />
      )}
    </CrmPageFrame>
  );
};

const Card = ({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) => (
  <Box component="section" aria-label={title} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', p: 2, display: 'grid', gap: 1.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Typography sx={{ fontWeight: 650, flex: 1 }}>{title}</Typography>
      {action}
    </Box>
    {children}
  </Box>
);

/** What it still needs before it can go, as a row of ticks. */
const Readiness = ({ ready, forWorkflow, highlight }: { ready: ReturnType<typeof draftProblems>; forWorkflow: boolean; highlight: boolean }) => {
  const items = (['AUDIENCE', 'SUBJECT', 'BODY', 'UNKNOWN_MERGE_FIELD'] as const).filter((k) => !(forWorkflow && k === 'AUDIENCE'));
  const done = ready.missing.length === 0;
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        flexWrap: 'wrap',
        alignItems: 'center',
        p: 1.25,
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: done ? alpha('#059669', 0.3) : highlight ? alpha('#d97706', 0.4) : 'divider',
        bgcolor: done ? alpha('#059669', 0.05) : 'background.paper',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 650, mr: 0.5 }}>
        {done ? 'Ready to send' : 'Before it can go'}
      </Typography>
      {items.map((k) => {
        const ok = !ready.missing.includes(k);
        return (
          <Chip
            key={k}
            size="small"
            icon={ok ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
            label={k === 'UNKNOWN_MERGE_FIELD' ? (ok ? 'Merge fields OK' : `Fix ${ready.fields.length} merge field${ready.fields.length === 1 ? '' : 's'}`) : MISSING_LABEL[k].split(':')[0]}
            sx={{
              bgcolor: ok ? alpha('#059669', 0.1) : '#f1f5f9',
              color: ok ? '#047857' : 'text.secondary',
              '& .MuiChip-icon': { color: ok ? '#059669' : '#94a3b8', fontSize: 16 },
            }}
          />
        );
      })}
    </Box>
  );
};
